import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
  addLayerToMap,
  getAllClickedFeatures,
  getAllFeaturesByDetail,
  removeComponentLayers,
  zoomToGeojsonGeometryWithBuffer,
} from 'utils/map/map';
import { createLayer } from 'utils/map/layers';
import { hasArrayJustOneElement, hasObjectKeys } from 'utils/main';
import { setSelectedZones } from 'store/actions/dashboard/selectedZones';
import { Grid, Typography } from '@material-ui/core';
import { formStyles } from 'consts/styles/Form';
import DashboardPagination from 'components/common/DashboardPagination';
import {
  addZonesFeaturesToLayer,
  getZonesByCategory,
  getZonesByType,
  setBasicZonesStyles,
  setInactiveZonesStyle,
  setSelectedZoneStyle,
} from './utils/zoneDetailsUtils';
import DashboardZoneDetails from './DashboardZoneDetails';

const ZoneDetails = props => {
  const { zonesTypesToDisplay, zonesCategoriesToDisplay } = props;

  const allZones = useSelector(state => state.allZones);
  const strings = useSelector(state => state.language).zonesDetails;
  const allLocalZones = useSelector(state => state.allLocalZones);
  const selectedZones = useSelector(state => state.selectedZones);
  const zonesCategories = useSelector(state => state.zonesCategories);
  const map = useSelector(state => state.map);

  const [selectedZonesNumberPrev, setSelectedZonesNumberPrev] = React.useState(
    0
  );
  const [zoneToDisplay, setZoneToDisplay] = React.useState(null);
  const [paginationNumber, setPaginationNumber] = React.useState(1);

  const dispatch = useDispatch();

  const zoneLayer = React.useRef(null);

  const formClasses = formStyles();

  const mapInit = () => {
    if (!hasObjectKeys(map)) return;
    zoneLayer.current = createLayer('zoneLayer');
    addLayerToMap(map, zoneLayer.current);
    map.on('click', handleClick);
    return () => cleanup();
  };

  React.useEffect(mapInit, [map]);

  const handleClick = evt => {
    const zonesFeatures = getAllFeaturesByDetail(
      getAllClickedFeatures(map, evt),
      'zone'
    );
    dispatch(setSelectedZones(zonesFeatures));
  };

  const cleanup = () => {
    removeComponentLayers(map);
    map.un('click', handleClick);
  };

  React.useEffect(() => {
    if (
      !zoneLayer.current ||
      !(Object.keys(allZones).length || Object.keys(allLocalZones).length)
    )
      return;
    const zonesToDisplay = getZonesByType(
      { ...allZones, ...allLocalZones },
      zonesTypesToDisplay
    );
    if (zonesCategoriesToDisplay.length !== zonesCategories.length) {
      addZonesFeaturesToLayer(
        zoneLayer.current,
        getZonesByCategory(zonesToDisplay, zonesCategoriesToDisplay)
      );
    } else addZonesFeaturesToLayer(zoneLayer.current, zonesToDisplay);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLocalZones, map, zonesTypesToDisplay, zonesCategoriesToDisplay]);

  React.useEffect(() => {
    const zonesToDisplay = getZonesByType(
      { ...allZones, ...allLocalZones },
      zonesTypesToDisplay
    );
    addZonesFeaturesToLayer(zoneLayer.current, zonesToDisplay);
  }, [zonesTypesToDisplay]);

  const handleSelectedZonesChange = () => {
    if (!hasArrayJustOneElement(selectedZones)) {
      if (selectedZonesNumberPrev === 1) setBasicZonesStyles(zoneLayer.current);
    } else {
      setInactiveZonesStyle(zoneLayer.current);
      setSelectedZoneStyle(selectedZones);
    }
    setSelectedZonesNumberPrev(selectedZones.length);
  };

  React.useEffect(handleSelectedZonesChange, [selectedZones]);

  const getZoneDetailsInfo = zone => {
    const zones = { ...allZones, ...allLocalZones };
    if (zone !== undefined) {
      const { type, name } = zone.getProperties();
      const foundZone = zones[type].find(z => z.name === name);
      return foundZone;
    }
  };

  const zoomToZone = geojson => {
    zoomToGeojsonGeometryWithBuffer(map, geojson);
  };

  const select = selectedZoneUid => {
    const usersSelectZone = selectedZones.find(
      z => z.getId() === selectedZoneUid
    );
    if (usersSelectZone) dispatch(setSelectedZones([usersSelectZone]));
  };

  React.useEffect(() => {
    if (!selectedZones.length) return;
    setZoneToDisplay(getZoneDetailsInfo(selectedZones[0]));
  }, [selectedZones]);

  React.useEffect(() => {
    if (!zoneToDisplay) return;
    setZoneToDisplay(getZoneDetailsInfo(selectedZones[paginationNumber - 1]));
  }, [paginationNumber]);

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent="flex-end"
        alignItems="center"
      >
        <Grid item xs={12}>
          {selectedZones.length && zoneToDisplay ? (
            <DashboardZoneDetails
              zoneToDisplay={zoneToDisplay}
              zoomToZone={zoomToZone}
              select={select}
            />
          ) : (
            <Typography
              variant="h3"
              className={`${formClasses.Form}__text-field__title ${formClasses.Form}__text-field`}
            >
              {strings.noItems_}
            </Typography>
          )}
        </Grid>
        {selectedZones.length > 1 && (
          <DashboardPagination
            paginationNumber={paginationNumber}
            setPaginationNumber={setPaginationNumber}
            paginationCounter={selectedZones.length}
          />
        )}
      </Grid>
    </>
  );
};

ZoneDetails.propTypes = {
  zonesTypesToDisplay: PropTypes.array.isRequired,
  zonesCategoriesToDisplay: PropTypes.array.isRequired,
};

export default ZoneDetails;
